import { useMemo } from "react";
import { useNavigate, useMatch } from "react-router-dom";
import { buildProductStepPath } from "../paths";
import type { ProductWorkflowState, WorkflowStepState } from "./types";
import type { WorkflowStep } from "../registry/configurations";
import { PRODUCT_STEP_ROUTE_PATTERN } from "../routePatterns";
import type { StepKeys } from "../steps/types";
import type { WorkflowProduct } from "../registry/configurations/ProductWorkflowConfiguration";

export type UseProductWorkflowArgs<TStepKey extends StepKeys> = {
  steps: ReadonlyArray<WorkflowStep<TStepKey>>;
  orderId: string;
  productId: string;
  product: WorkflowProduct;
  completedStepKeys?: ReadonlyArray<TStepKey>;
  onComplete: () => void;
  /**
   * Manual mode to provide the currentn step instead of deriving from url
   */
  currentStepKey?: TStepKey;
  /**
   * Manual mode to override navigation
   */
  onGo?: (target: { productId: string; stepKey: TStepKey }) => void;
  bulkProductIds?: ReadonlyArray<string>;
};

export function useProductWorkflow<TStepKey extends StepKeys>({
  steps,
  orderId,
  productId,
  completedStepKeys = [],
  onComplete,
  currentStepKey: currentStepKeyProp,
  onGo,
  bulkProductIds,
  product,
}: UseProductWorkflowArgs<TStepKey>): ProductWorkflowState<TStepKey> {
  const navigate = useNavigate();

  const match = useMatch(PRODUCT_STEP_ROUTE_PATTERN);

  const currentStepKey =
    currentStepKeyProp ??
    (match?.params.productId === productId ? match.params.step : undefined);

  const activeSteps = useMemo<
    ReadonlyArray<WorkflowStepState<TStepKey>>
  >(() => {
    const filtered = steps.filter(s => !s.isSkipped(product));
    const completedSet = new Set<string>(completedStepKeys);

    return filtered.map((step, index) => {
      const previousStep = index > 0 ? filtered[index - 1] : undefined;
      const reachable =
        index === 0 ||
        (previousStep ? completedSet.has(previousStep.key) : false);

      return {
        ...step,
        completed: completedSet.has(step.key),
        reachable,
      };
    });
  }, [steps, completedStepKeys, product]);

  if (activeSteps.length === 0) {
    throw new Error("useProductWorkflow: at least one active step is required");
  }

  const matchedIndex = activeSteps.findIndex(s => s.key === currentStepKey);
  const safeIndex = matchedIndex >= 0 ? matchedIndex : 0;
  const currentStep = activeSteps[safeIndex];
  const isFirst = safeIndex === 0;
  const isLast = safeIndex === activeSteps.length - 1;

  const goTo = (stepKey: TStepKey) => {
    if (onGo) {
      onGo({ productId, stepKey });
      return;
    }
    navigate(buildProductStepPath(orderId, productId, stepKey, bulkProductIds));
  };

  const goToFirst = () => {
    goTo(activeSteps[0].key);
  };

  const goBack = () => {
    if (!isFirst) {
      goTo(activeSteps[safeIndex - 1].key);
    }
  };

  const goNext = () => {
    if (isLast) {
      onComplete();
      return;
    }
    goTo(activeSteps[safeIndex + 1].key);
  };

  return {
    currentStep,
    activeSteps,
    stepIndex: safeIndex,
    totalSteps: activeSteps.length,
    isFirst,
    isLast,
    goTo,
    goToFirst,
    goBack,
    goNext,
  };
}
